import React, { useEffect, useRef, useState } from 'react'
import {
  AppleAppStoreIcon,
  ConfigProvider,
  GooglePlayStoreIcon,
  Text,
} from '@ntuctech/tangram-web'
import tangramNextConfig from '../../lib/tangramNextConfig'
import styled from 'styled-components'
import Expand from '../icons/ExpandNew'
import dynamic from 'next/dynamic'
import Image from '../Image/Image'
import { getConfigFor } from '../../utils/configService'
import { SPLIT_FEATURES } from '../../utils/constants'
import { SCREEN_SIZE } from '../../lib/Media'
import { useRouter } from 'next/router'
const Popover = dynamic(() => import('react-tiny-popover'))

const GetAppWrapper = styled.button`
  padding: 0;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: ${({ $deliveryModeB2B }) => ($deliveryModeB2B ? '#FFF' : '#333333')};
`

const PopoverContainer = styled.div`
  ${({ $rect }) =>
    $rect
      ? `
    position: fixed;
    top: ${$rect.bottom + 6}px;
    left: ${$rect.left + $rect.width / 2}px;
    transform: translateX(-50%);
  `
      : ''}
  margin: auto;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 4px;
  box-shadow: 0 13px 15px 0 rgba(0, 0, 0, 0.12);
  background-color: #ffffff;
  width: 26.875rem;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.375rem;

  ${SCREEN_SIZE.Below.MobileL} {
    display: none;
  }
`

const PopoverWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
`

const PopoverContent = styled(Text)`
  margin-top: 6px;
`

const PopoverDownloadButtonContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  > a > svg {
    height: 32px;
    width: 100px;
  }
`

const PopoverQrContainer = styled.div`
  width: 7.625rem;
  height: 7.625rem;
`

const StyledExpandIcon = styled(Expand)`
  display: none;

  ${SCREEN_SIZE.From.Desktop} {
    width: 1rem;
    height: 1rem;
    display: block;
  }
`

export default function GetApp({ deliveryModeB2B = false }) {
  const { isEnabled: isGetAppMenuEnabled, configuration: getAppConfiguration } =
    getConfigFor(SPLIT_FEATURES.FE_ENGM_GET_APP_MENU)
  const [isShownPopover, setShownPopover] = useState(false)
  const [wrapperRect, setWrapperRect] = useState(null)
  const labelRef = useRef(null)
  const appleAppStoreUrlRef = useRef(null)
  const goolePlayStoreUrlRef = useRef(null)
  const router = useRouter()
  const isHomepage = router.pathname === '/'
  const shownAtScrollY = useRef(0)

  useEffect(() => {
    if (isHomepage) {
      setShownPopover(true)
    }
  }, [isHomepage])

  useEffect(() => {
    if (isShownPopover) {
      shownAtScrollY.current = window.scrollY
      updatePopoverPosition()
      window.addEventListener('resize', updatePopoverPosition)
      window.addEventListener('scroll', updatePopoverPosition)
    }

    return () => {
      window.removeEventListener('resize', updatePopoverPosition)
      window.removeEventListener('scroll', updatePopoverPosition)
    }
  }, [isShownPopover])

  useEffect(() => {
    const handleTab = e => {
      if (e.key === 'Tab' && isShownPopover) {
        if (e.target === labelRef.current && !e.shiftKey) {
          e.preventDefault()
          appleAppStoreUrlRef.current?.focus()
        }

        if (e.target === labelRef.current && e.shiftKey) {
          setShownPopover(false)
        }

        if (e.target === appleAppStoreUrlRef.current && e.shiftKey) {
          e.preventDefault()
          labelRef.current?.focus()
          setShownPopover(false)
        }

        if (e.target === goolePlayStoreUrlRef.current && !e.shiftKey) {
          e.preventDefault()
          labelRef.current?.focus()
          setShownPopover(false)
        }
      }
    }

    window?.document?.addEventListener('keydown', handleTab)

    return () => {
      window?.document?.removeEventListener('keydown', handleTab)
    }
  }, [isShownPopover])

  const updatePopoverPosition = () => {
    if (labelRef.current) {
      const rect = labelRef.current.getBoundingClientRect()
      setWrapperRect(rect)
    }
    // onscroll up change the anchor position
    if (window.scrollY < shownAtScrollY.current) {
      shownAtScrollY.current = window.scrollY
    }
    // Close the popover if the user scrolls down
    if (window.scrollY - shownAtScrollY.current > 50) {
      setShownPopover(false)
    }
  }

  if (!isGetAppMenuEnabled) {
    return null
  }

  return (
    <ConfigProvider
      tokens={tangramNextConfig}
      experimental={{
        Text: true,
      }}
    >
      <Popover
        isOpen={isShownPopover}
        position={['bottom']}
        onClickOutside={() => {
          setShownPopover(false)
        }}
        content={
          <PopoverContainer
            id="get-app-popover"
            role="dialog"
            aria-modal="false"
            aria-hidden="true"
            onMouseEnter={() => {
              setShownPopover(true)
            }}
            onFocus={() => {
              setShownPopover(true)
            }}
            $rect={wrapperRect}
          >
            <PopoverWrapper>
              <Text size="m" bold>
                {getAppConfiguration.title}
              </Text>

              <PopoverContent size="s">
                {getAppConfiguration.content}
              </PopoverContent>

              <PopoverDownloadButtonContainer>
                <a
                  ref={appleAppStoreUrlRef}
                  href={getAppConfiguration.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="getapp_apple"
                >
                  <AppleAppStoreIcon />
                </a>
                <a
                  ref={goolePlayStoreUrlRef}
                  href={getAppConfiguration.playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="getapp_android"
                >
                  <GooglePlayStoreIcon />
                </a>
              </PopoverDownloadButtonContainer>
            </PopoverWrapper>

            <PopoverQrContainer>
              <Image src={getAppConfiguration.qrImage} />
            </PopoverQrContainer>
          </PopoverContainer>
        }
      >
        <GetAppWrapper
          aria-haspopup="dialog"
          aria-expanded="false"
          aria-controls="get-app-popover"
          ref={labelRef}
          $deliveryModeB2B={deliveryModeB2B}
          onClick={() => {
            setShownPopover(isPreviouslyShown => !isPreviouslyShown)
          }}
          onMouseOver={() => {
            setShownPopover(true)
          }}
        >
          <Text size="s" color="inherit">
            {getAppConfiguration.label}
          </Text>
          <StyledExpandIcon color={deliveryModeB2B ? '#FFF' : '#696969'} />
        </GetAppWrapper>
      </Popover>
    </ConfigProvider>
  )
}
